import React, { useEffect, useMemo } from "react";
import { Alert, Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import CustomDataGrid from "../Shared/Table/CustomDataGrid";
import { useGetTemplatePreviewQuery } from "../../store/api/tasks/taskApiSlice";
import { convertSymbols } from "../../utils/helpers";
import ExportTemplate from "../../pages/Tasks/ExportTemplate";
import { Sheet } from "../ui/sheet";
import { Icon } from "../Shared/Icon";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../store/helpers/helperSelectors";
import { initialState } from "../../store/helpers/helperInitialState";
import { useStoreDispatch } from "../../store/hooks";
import { setPaginationModel } from "../../store/helpers/helperReducers";

interface TemplateAttributesDrawerProps {
  open: boolean;
  onClose: () => void;
  templateId: string;
  templateDisplayName: string;
}

type TemplateAttributeRow = {
  __rowId: number;
  attribute: string;
  value: string;
};

const TABLE_ID = "templateAttributesDrawer";

const TemplateAttributesDrawer: React.FC<TemplateAttributesDrawerProps> = ({
  open,
  onClose,
  templateId,
  templateDisplayName,
}) => {
  const dispatch = useStoreDispatch();
  const paginationModel = useSelector(paginationModelSelector);
  const currentPageSize = paginationModel[TABLE_ID]?.pageSize ?? initialState.pagination.pageSize;

  useEffect(() => {
    if (!open || !templateId) {
      return;
    }
    dispatch(setPaginationModel({ [TABLE_ID]: { page: 0, pageSize: currentPageSize } }));
  }, [dispatch, open, templateId, currentPageSize]);

  const currentPagination = paginationModel[TABLE_ID] ?? initialState.pagination;

  const { data, isLoading, isFetching, isError, error } = useGetTemplatePreviewQuery(
    {
      templateId,
      page: currentPagination.page,
      size: currentPagination.pageSize,
    },
    {
      skip: !open || !templateId,
      refetchOnMountOrArgChange: true,
    }
  );

  const rows: TemplateAttributeRow[] = useMemo(() => {
    const previewRows = data?.rows;
    if (!Array.isArray(previewRows)) {
      return [];
    }
    return previewRows.map((entry: any, index: number) => ({
      __rowId: Number(entry?.__rowId ?? index),
      attribute: String(entry?.attribute ?? ""),
      value: String(entry?.value ?? ""),
    }));
  }, [data]);

  const totalRows = data?.totalRows ?? 0;
  const isPreviewLoading = isLoading || isFetching;

  const drawerErrorMessage = useMemo(() => {
    if (!isError) return "";
    if (typeof (error as any)?.data === "string" && (error as any).data.trim().length > 0) {
      return (error as any).data;
    }
    return "Failed to load template attributes.";
  }, [error, isError]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      width="clamp(900px, 70vw, 100vw)"
      useCardStyle={false}
      showCloseButton={false}
    >
      <style>{`.MuiPopover-root, .MuiMenu-root { z-index: 1400 !important; }`}</style>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0, gap: 2 }}>
        <div className="flex items-start justify-between border-b border-border pb-4 mb-1">
          <div className="flex flex-col gap-1 min-w-0">
            <Typography variant="h5" title={templateDisplayName} sx={{ m: 0, lineHeight: 1.2 }}>
              {convertSymbols(templateDisplayName)}
            </Typography>
            <Typography variant="body2">Template Attributes ({totalRows})</Typography>
          </div>
          <div className="flex flex-col items-center gap-2 shrink-0 ml-4">
            <button
              onClick={onClose}
              aria-label="Close"
              className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-transparent text-foreground border-0 p-0 focus-visible:outline-none cursor-pointer"
            >
              <Icon name="close" size={20} />
            </button>
            <Box>
              <ExportTemplate selectedRow={{ templateName: templateDisplayName }} />
            </Box>
          </div>
        </div>

        {isError && <Alert severity="error">{convertSymbols(drawerErrorMessage)}</Alert>}

        {isPreviewLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading template attributes...</Typography>
          </Box>
        )}

        {!isPreviewLoading && !isError && rows.length === 0 && (
          <Alert severity="info">No attributes found for this template.</Alert>
        )}

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <CustomDataGrid
            tableId={TABLE_ID}
            rows={rows}
            columns={[
              {
                field: "attribute",
                headerName: "Attribute",
                flex: 1,
                minWidth: 320,
                sortable: false,
                renderCell: (cellValues: any) => {
                  const attributeValue = cellValues.value == null ? "" : String(cellValues.value);
                  return (
                    <Tooltip title={attributeValue} enterDelay={300} placement="bottom-start">
                      <span
                        tabIndex={cellValues.hasFocus ? 0 : -1}
                        className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-mono text-muted-foreground"
                      >
                        {convertSymbols(attributeValue)}
                      </span>
                    </Tooltip>
                  );
                },
              },
              {
                field: "value",
                headerName: "Value",
                flex: 0.5,
                minWidth: 180,
                sortable: false,
                renderCell: (cellValues: any) => {
                  const valueText = cellValues.value == null ? "" : String(cellValues.value);
                  return (
                    <Tooltip title={valueText} enterDelay={300} placement="bottom-start">
                      <span
                        tabIndex={cellValues.hasFocus ? 0 : -1}
                        className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-mono text-muted-foreground"
                      >
                        {convertSymbols(valueText)}
                      </span>
                    </Tooltip>
                  );
                },
              },
            ]}
            rowCount={totalRows}
            loading={isPreviewLoading}
            disableColumnMenu={true}
            disableRowSelectionOnClick={true}
            paginationMode="server"
            sortingMode="client"
            getRowId={(row) => row.__rowId}
            sx={{ height: "100%" }}
          />
        </Box>
      </Box>
    </Sheet>
  );
};

export default TemplateAttributesDrawer;
